import { I18n } from "@assets/strings";
import React from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { ProfileRole } from "@domain/user/profile";
import { useAsync } from "@utils/hooks";
import { gap } from "@ui/styles/utils";
import { OnboardingDisconnect } from "./common";

type ProfileRoleStepProps = React.HTMLAttributes<HTMLDivElement>;

export const ProfileRoleStep: React.FC<ProfileRoleStepProps> = ({ ...htmlProps }) => {
	const { currentProfileService } = useServices();
	const { run, loading } = useAsync((role: ProfileRole) => currentProfileService.finishOnboarding(role), []);

	return (
		<Container {...htmlProps}>
			<Title>{I18n.t("auth.role.title")}</Title>
			<Lead>{I18n.t("auth.role.lead")}</Lead>
			<Choices>
				<Choice type="button" disabled={loading} onClick={() => run("LEARNER")}>
					<IconBadge>
						<LearnerIcon />
					</IconBadge>
					<ChoiceText>
						<ChoiceTitle>{I18n.t("auth.role.learner")}</ChoiceTitle>
						<span>{I18n.t("auth.role.learnerLead")}</span>
					</ChoiceText>
				</Choice>
				<Choice type="button" disabled={loading} onClick={() => run("TEACHER")}>
					<IconBadge>
						<TeacherIcon />
					</IconBadge>
					<ChoiceText>
						<ChoiceTitle>{I18n.t("auth.role.teacher")}</ChoiceTitle>
						<span>{I18n.t("auth.role.teacherLead")}</span>
					</ChoiceText>
				</Choice>
				<Choice type="button" disabled={loading} onClick={() => run("PARENT")}>
					<IconBadge>
						<ParentIcon />
					</IconBadge>
					<ChoiceText>
						<ChoiceTitle>{I18n.t("auth.role.parent")}</ChoiceTitle>
						<span>{I18n.t("auth.role.parentLead")}</span>
					</ChoiceText>
				</Choice>
			</Choices>
			<Disconnect>
				<OnboardingDisconnect />
			</Disconnect>
		</Container>
	);
};

const Container = styled.div`
	${gap(16)};
	width: 100%;
`;

const Title = styled.h1`
	margin: 0 0 6px;
	font-size: 28px;
	font-weight: 700;
	letter-spacing: -0.4px;
	line-height: 1.2;
	color: #13131a;
`;

const Lead = styled.p`
	margin: 0 0 8px;
	color: #8a94a4;
	font-size: 15px;
	line-height: 1.5;
`;

const Disconnect = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 8px;
`;

const Choices = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const Choice = styled.button`
	cursor: pointer;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 14px;
	text-align: left;
	border-radius: 20px;
	border: 1.5px solid #e3e7ec;
	background: #fff;
	padding: 16px 18px;
	color: #8a94a4;
	font-size: 13px;
	line-height: 1.4;

	&:hover {
		border-color: #00863d;
		background: #f4fbf7;
	}

	&:disabled {
		pointer-events: none;
		opacity: 0.6;
	}
`;

const ChoiceText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

const ChoiceTitle = styled.b`
	display: block;
	font-size: 15.5px;
	font-weight: 600;
	color: #13131a;
`;

const IconBadge = styled.span`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	border-radius: 16px;
	background: #e7f4ec;
	color: #00863d;
`;

const LearnerIcon = (): React.ReactElement => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinejoin="round"
	>
		<path d="M12 4 2 9l10 5 10-5-10-5Z" />
		<path d="M6 11v4c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-4" />
	</svg>
);

const ParentIcon = (): React.ReactElement => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="8" cy="7" r="2.4" />
		<circle cx="16.2" cy="8.2" r="1.8" />
		<path d="M3.2 19v-.8a3.6 3.6 0 0 1 3.6-3.6h2.4a3.6 3.6 0 0 1 3.6 3.6V19" />
		<path d="M14.2 19v-.4a2.8 2.8 0 0 1 2.8-2.8h.4a2.8 2.8 0 0 1 2.8 2.8V19" />
	</svg>
);

const TeacherIcon = (): React.ReactElement => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="6.5" cy="14" r="3.5" />
		<circle cx="17.5" cy="14" r="3.5" />
		<path d="M10 14h4M3 12l1.6-3.2h2M21 12l-1.6-3.2h-2" />
	</svg>
);
